import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { useNavigate } from 'react-router';
import { useTranslations } from '@/i18n/use-locale';
import { useLocalizedName } from '@/i18n/localized-name';
import { formatCount } from '@/lib/format';
import { QueryGate } from '@/components/ui/query-gate';
import { useAnalyticsGrowthQuery, useAnalyticsSummaryQuery } from '@/lib/queries';
import { Card } from '@/components/ui/card';
import { cn } from '@/lib/utils';

/** The donut segment colours, in order — one per plan tier. */
const SEGMENT_COLORS = ['#172514', '#506836', '#aab6a1', '#354a28', '#7a9b54'];

/** One slice of the plan distribution bar: its share, and where along the bar it starts. */
interface PlanSegment {
  key: string;
  name: string;
  count: number;
  pct: number;
  offset: number;
  color: string | undefined;
}

/** An SVG line + filled area for one series, scaled into the 500×200 viewBox. */
function series(values: number[], max: number): { line: string; area: string } {
  const n = values.length;
  if (n === 0) return { line: '', area: '' };
  const x = (i: number) => (n === 1 ? 250 : (i / (n - 1)) * 500);
  const y = (v: number) => 180 - (max === 0 ? 0 : (v / max) * 160);
  const points = values.map((v, i) => `${x(i).toFixed(1)},${y(v).toFixed(1)}`);
  return {
    line: `M${points.join(' L')}`,
    area: `M${x(0).toFixed(1)},200 L${points.join(' L')} L${x(n - 1).toFixed(1)},200 Z`,
  };
}

export function DashboardCharts() {
  const navigate = useNavigate();
  const t = useTranslations('admin.charts');
  const localizedName = useLocalizedName();
  const [range, setRange] = useState<6 | 12>(6);

  // The summary is the dashboard's own query, already resolved by the time this
  // renders; the growth series is this block's, and gates it.
  const summaryQuery = useAnalyticsSummaryQuery();
  const growthQuery = useAnalyticsGrowthQuery(range);

  return (
    <QueryGate query={growthQuery}>
      {(growth) => {
        const maxGrowth = Math.max(1, ...growth.flatMap((g) => [g.talents, g.organizations]));
        const talents = series(
          growth.map((g) => g.talents),
          maxGrowth,
        );
        const clubs = series(
          growth.map((g) => g.organizations),
          maxGrowth,
        );

        const distribution = summaryQuery.data?.planDistribution ?? [];
        const totalSubs = distribution.reduce((acc, p) => acc + p.count, 0);
        // Each segment starts where the previous one ended; a loop keeps that running
        // total in plain sight, where a `map` closure would hide the reassignment.
        const segments: PlanSegment[] = [];
        let offset = 0;
        for (const [i, p] of distribution.entries()) {
          const pct = totalSubs === 0 ? 0 : (p.count / totalSubs) * 100;
          segments.push({
            key: p.planId,
            name: localizedName(p),
            count: p.count,
            pct,
            offset,
            color: SEGMENT_COLORS[i % SEGMENT_COLORS.length],
          });
          offset += pct;
        }

        return (
          <div className="grid gap-6 lg:grid-cols-3">
            {/* Platform Growth Chart */}
            <Card className="p-5 lg:col-span-2">
              <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
                <div>
                  <h3 className="text-[15px] font-bold text-navy">{t('growth.title')}</h3>
                  <p className="text-[12.5px] text-conc-500">{t('growth.versus')}</p>
                </div>
                <div className="flex rounded-lg bg-sideline p-1">
                  {(
                    [
                      [6, t('growth.months', { count: formatCount(6) })],
                      [12, t('growth.year')],
                    ] as const
                  ).map(([months, label]) => (
                    <Button
                      key={months}
                      type="button"
                      variant="ghost"
                      size="none"
                      aria-pressed={range === months}
                      onClick={() => setRange(months)}
                      className={cn(
                        'rounded-md px-3 py-1 text-[12px]',
                        range === months
                          ? 'bg-white text-navy shadow-sm hover:bg-white'
                          : 'text-conc-500 hover:bg-transparent hover:text-navy',
                      )}
                    >
                      {label}
                    </Button>
                  ))}
                </div>
              </div>

              <div className="relative h-64 w-full rounded-xl bg-sideline p-4">
                {growth.length === 0 ? (
                  <div className="grid h-full place-items-center text-[13px] text-conc-400">
                    {t('growth.empty')}
                  </div>
                ) : (
                  <svg className="h-full w-full" viewBox="0 0 500 200" preserveAspectRatio="none">
                    <defs>
                      <linearGradient id="pitchGrad" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#172514" stopOpacity="0.25" />
                        <stop offset="100%" stopColor="#172514" stopOpacity="0.0" />
                      </linearGradient>
                      <linearGradient id="goldGrad" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#506836" stopOpacity="0.2" />
                        <stop offset="100%" stopColor="#506836" stopOpacity="0.0" />
                      </linearGradient>
                    </defs>

                    {[50, 100, 150].map((y) => (
                      <line
                        key={y}
                        x1="0"
                        y1={y}
                        x2="500"
                        y2={y}
                        stroke="#c5cebc"
                        strokeDasharray="3 3"
                        strokeWidth="0.5"
                      />
                    ))}

                    <path d={talents.area} fill="url(#pitchGrad)" />
                    <path d={talents.line} fill="none" stroke="#172514" strokeWidth="3" />
                    <path d={clubs.area} fill="url(#goldGrad)" />
                    <path
                      d={clubs.line}
                      fill="none"
                      stroke="#506836"
                      strokeWidth="2.5"
                      strokeDasharray="4 3"
                    />
                  </svg>
                )}

                <div className="absolute end-4 bottom-3 flex gap-4 text-[12px]">
                  <div className="flex items-center gap-1.5 font-semibold text-pitch">
                    <span className="h-2.5 w-2.5 rounded-full bg-pitch" />
                    {t('growth.legendTalents')}
                  </div>
                  <div className="flex items-center gap-1.5 font-semibold text-gold-700">
                    <span className="h-2.5 w-2.5 rounded-full bg-gold" />
                    {t('growth.legendOrganizations')}
                  </div>
                </div>
              </div>
            </Card>

            {/* Plan Distribution Donut Chart */}
            <Card className="p-5">
              <h3 className="mb-1 text-[15px] font-bold text-navy">{t('plans.title')}</h3>
              <p className="mb-4 text-[12.5px] text-conc-500">{t('plans.subtitle')}</p>

              <div className="relative mx-auto my-4 grid h-44 w-44 place-items-center">
                <svg className="h-full w-full -rotate-90" viewBox="0 0 36 36">
                  <path
                    d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
                    fill="none"
                    stroke="#dde3d7"
                    strokeWidth="3.8"
                  />
                  {segments.map((seg) => (
                    <path
                      key={seg.key}
                      d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
                      fill="none"
                      stroke={seg.color}
                      strokeWidth="3.8"
                      strokeDasharray={`${seg.pct}, 100`}
                      strokeDashoffset={`-${seg.offset}`}
                    />
                  ))}
                </svg>
                <div className="absolute text-center">
                  <p className="num text-xl font-bold text-navy">{formatCount(totalSubs)}</p>
                  <p className="text-[11px] text-conc-500">{t('plans.subscribers')}</p>
                </div>
              </div>

              <div className="space-y-2 text-[12.5px]">
                {segments.map((seg) => (
                  <Button
                    key={seg.key}
                    type="button"
                    variant="ghost"
                    size="none"
                    onClick={() => void navigate('/subscriptions')}
                    className="flex w-full justify-between p-2 text-start font-normal whitespace-normal"
                  >
                    <span className="flex items-center gap-2 font-semibold text-navy">
                      <span
                        className="h-2.5 w-2.5 rounded-full"
                        style={{ backgroundColor: seg.color }}
                      />
                      {seg.name}
                    </span>
                    <span className="num font-bold text-navy">
                      {t('plans.share', {
                        count: formatCount(seg.count),
                        pct: formatCount(Math.round(seg.pct)),
                      })}
                    </span>
                  </Button>
                ))}
                {segments.length === 0 && (
                  <p className="py-4 text-center text-[13px] text-conc-400">{t('plans.empty')}</p>
                )}
              </div>
            </Card>
          </div>
        );
      }}
    </QueryGate>
  );
}
